import React, { useMemo, useState } from 'react';
import { ChevronRight } from 'lucide-react';

export const ACTION_LABELS = {
  create: 'Create new',
  match_existing: 'Match existing',
  replace: 'Replace previous',
};

const ACTION_BADGES = {
  create: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  match_existing: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
  replace: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
};

const groupBy = (rows, keyFn) => {
  const out = {};
  (rows || []).forEach((row) => {
    const key = keyFn(row);
    if (!key) return;
    (out[key] = out[key] || []).push(row);
  });
  return out;
};

const Chip = ({ children }) => (
  <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">
    {children}
  </span>
);

const ActionBadge = ({ action }) =>
  action ? (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${ACTION_BADGES[action] || ACTION_BADGES.match_existing}`}>
      {ACTION_LABELS[action] || action}
    </span>
  ) : null;

const Row = ({ primary, chips = [], detail }) => (
  <li className="py-1.5 flex items-start justify-between gap-3">
    <div className="min-w-0">
      <p className="text-sm text-gray-900 dark:text-gray-100 truncate">{primary}</p>
      {detail && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{detail}</p>}
    </div>
    <div className="flex gap-1 shrink-0">{chips.map((c, i) => <Chip key={i}>{c}</Chip>)}</div>
  </li>
);

const PlannedChangesReview = ({ payload, plannedActions = {}, classification }) => {
  const [open, setOpen] = useState(() => new Set());

  const toggle = (key) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const model = useMemo(() => {
    if (!payload) return null;
    const refMap = {};
    Object.values(payload).forEach((rows) =>
      (rows || []).forEach((row) => {
        if (row?.ref) refMap[row.ref] = row;
      })
    );
    const screenQuestionsByScreen = groupBy(payload.screen_question, (sq) => sq.screen);
    const navRulesByScreen = groupBy(
      payload.navigation_rule,
      (r) => refMap[r.screen_question]?.screen
    );
    return {
      refMap,
      assessmentsBySurvey: groupBy(payload.assessment, (a) => a.survey),
      questionsByAssessment: groupBy(payload.question, (q) => q.assessment),
      questionsBySurvey: groupBy(payload.question, (q) => q.survey),
      screensBySurvey: groupBy(payload.screen, (s) => s.survey),
      screenQuestionsByScreen,
      navRulesByScreen,
      scoringByAssessment: groupBy(payload.scoring_rule, (r) => r.assessment),
      versionsBySchedule: groupBy(payload.schedule_version, (v) => v.schedule),
    };
  }, [payload]);

  if (!payload || !model) return null;

  const { refMap } = model;
  const study = (payload.study || [])[0];
  const mfa = (payload.mfa_settings || [])[0];
  const surveys = payload.survey || [];
  const templates = [
    ...(payload.layout_template || []).map((t) => ({ ...t, _kind: 'Question layout' })),
    ...(payload.screen_layout_template || []).map((t) => ({ ...t, _kind: 'Screen layout' })),
    ...(payload.assessment_template || []).map((t) => ({ ...t, _kind: 'Assessment template' })),
  ];
  const variants = payload.form_variant || [];
  const schedules = payload.schedule || [];
  const reminders = payload.reminder_template || [];
  const references = [
    ...(payload.device || []).map((d) => ({ label: d.name, kind: 'Device', extra: d.device_type })),
    ...(payload.screen_template || []).map((t) => ({ label: t.name, kind: 'Screen template' })),
    ...(payload.vpg_question || []).map((q) => ({ label: q.text_preview || q.code, kind: 'VPG question' })),
  ];

  const Section = ({ id, title, count, action, children, note }) => (
    <div className="border-b border-gray-100 dark:border-gray-700 last:border-b-0">
      <button
        onClick={() => toggle(id)}
        className="w-full flex items-center gap-3 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 px-2 -mx-2 rounded transition-colors"
      >
        <ChevronRight
          className={`w-4 h-4 text-gray-400 shrink-0 transition-transform ${open.has(id) ? 'rotate-90' : ''}`}
        />
        <span className="text-sm font-medium text-gray-900 dark:text-gray-100 flex-1">
          {title}
        </span>
        {note && <span className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">{note}</span>}
        {count != null && <Chip>{count}</Chip>}
        <ActionBadge action={action} />
      </button>
      {open.has(id) && <div className="pb-3 pl-7">{children}</div>}
    </div>
  );

  const SubSection = ({ id, title, count, children }) => (
    <div className="mt-1">
      <button
        onClick={() => toggle(id)}
        className="flex items-center gap-2 py-1 text-left text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100"
      >
        <ChevronRight
          className={`w-3 h-3 text-gray-400 transition-transform ${open.has(id) ? 'rotate-90' : ''}`}
        />
        {title}
        <Chip>{count}</Chip>
      </button>
      {open.has(id) && (
        <ul className="ml-5 divide-y divide-gray-50 dark:divide-gray-700/50">{children}</ul>
      )}
    </div>
  );

  const settingRows = study
    ? [
        ['Study code', study.code],
        ['Title', study.title],
        ['Description', study.description],
        ['Device strategy', study.device_strategy],
        ['BYOD allowed', study.is_byod_allowed ? 'yes' : 'no'],
        ['Caregivers allowed', study.allow_caregivers ? 'yes' : 'no'],
        ['Enrollment without schedule', study.allow_enrollment_without_schedule ? 'yes' : 'no'],
        ['Participant portal title', study.participant_portal_title],
        ['Provisioned devices', (study.provisioned_devices || []).map((r) => refMap[r]?.name).filter(Boolean).join(', ')],
        mfa && ['MFA required for all users', mfa.mfa_required_for_all_users ? `yes (grace ${mfa.grace_period_days} days)` : 'no'],
      ].filter((row) => row && row[1] != null && row[1] !== '')
    : [];

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md dark:shadow-gray-900/20 p-6">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Planned changes
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {classification === 're_promotion'
            ? 'Re-promotion: the previous deployment’s content below is replaced in one transaction.'
            : 'Everything below is created new in one transaction.'}
        </p>
      </div>

      <Section id="study" title="Study record & settings" count={settingRows.length} action={plannedActions.study}>
        <dl className="text-sm">
          {settingRows.map(([label, value]) => (
            <div key={label} className="flex gap-3 py-1">
              <dt className="w-56 shrink-0 text-gray-500 dark:text-gray-400">{label}</dt>
              <dd className="text-gray-900 dark:text-gray-100 min-w-0 truncate">{String(value)}</dd>
            </div>
          ))}
        </dl>
      </Section>

      <Section
        id="surveys"
        title="Surveys & assessments"
        count={surveys.length}
        action={plannedActions.survey || plannedActions.assessment}
      >
        {surveys.map((survey) => {
          const assessments = model.assessmentsBySurvey[survey.ref] || [];
          const questions = assessments.flatMap((a) => model.questionsByAssessment[a.ref] || []);
          const seen = new Set(questions.map((q) => q.ref));
          (model.questionsBySurvey[survey.ref] || []).forEach((q) => {
            if (!seen.has(q.ref)) questions.push(q);
          });
          const screens = model.screensBySurvey[survey.ref] || [];
          const scoring = assessments.flatMap((a) => model.scoringByAssessment[a.ref] || []);
          return (
            <div key={survey.ref} className="mt-2 first:mt-0">
              <button
                onClick={() => toggle(survey.ref)}
                className="w-full flex items-center gap-2 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/40 px-2 -mx-2 rounded"
              >
                <ChevronRight
                  className={`w-3.5 h-3.5 text-gray-400 shrink-0 transition-transform ${open.has(survey.ref) ? 'rotate-90' : ''}`}
                />
                <span className="text-sm text-gray-900 dark:text-gray-100 truncate flex-1">
                  {survey.name}
                </span>
                {survey.language && <Chip>{survey.language}</Chip>}
                {survey.survey_type && <Chip>{survey.survey_type}</Chip>}
                <Chip>{questions.length} questions</Chip>
                <Chip>{screens.length} screens</Chip>
              </button>
              {open.has(survey.ref) && (
                <div className="ml-6">
                  {assessments.map((a) => (
                    <p key={a.ref} className="text-xs text-gray-500 dark:text-gray-400 py-0.5">
                      {a.assessment_type || 'assessment'} v{a.version}
                      {a.cdisc_compliant ? ' · CDISC compliant' : ''}
                      {(survey.linked_surveys || []).length
                        ? ` · linked to ${(survey.linked_surveys || []).map((r) => refMap[r]?.name).filter(Boolean).join(', ')}`
                        : ''}
                    </p>
                  ))}
                  <SubSection id={`${survey.ref}/questions`} title="Questions" count={questions.length}>
                    {questions.map((q) => (
                      <Row key={q.ref} primary={q.text} chips={[q.type]} detail={q.code} />
                    ))}
                  </SubSection>
                  <SubSection id={`${survey.ref}/screens`} title="Screens" count={screens.length}>
                    {screens.map((s) => {
                      const sq = model.screenQuestionsByScreen[s.ref] || [];
                      const nav = model.navRulesByScreen[s.ref] || [];
                      return (
                        <Row
                          key={s.ref}
                          primary={s.name}
                          chips={[`${sq.length} q`, `${nav.length} rules`]}
                          detail={s.template ? `template: ${refMap[s.template]?.name}` : null}
                        />
                      );
                    })}
                  </SubSection>
                  {scoring.length > 0 && (
                    <SubSection id={`${survey.ref}/scoring`} title="Scoring rules" count={scoring.length}>
                      {scoring.map((r) => (
                        <Row key={r.ref} primary={r.name} chips={[r.scoring_type]} detail={r.description} />
                      ))}
                    </SubSection>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </Section>

      {templates.length > 0 && (
        <Section id="templates" title="Layout & assessment templates" count={templates.length} action={plannedActions.layout_template || plannedActions.assessment_template}>
          <ul className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {templates.map((t) => (
              <Row
                key={t.ref}
                primary={t.name}
                chips={[t._kind]}
                detail={t.assessment ? `for ${refMap[refMap[t.assessment]?.survey]?.name || 'assessment'}` : t.question_type}
              />
            ))}
          </ul>
        </Section>
      )}

      {variants.length > 0 && (
        <Section id="variants" title="Form variants (languages & devices)" count={variants.length} action={plannedActions.form_variant}>
          <ul className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {variants.map((v) => (
              <Row
                key={v.ref}
                primary={refMap[refMap[v.primary_assessment]?.survey]?.name || 'assessment'}
                chips={[v.language, refMap[v.device]?.name].filter(Boolean)}
                detail={v.is_translation_complete ? 'translation complete' : 'translation incomplete'}
              />
            ))}
          </ul>
        </Section>
      )}

      {schedules.length > 0 && (
        <Section id="schedules" title="Schedules" count={schedules.length} action={plannedActions.schedule}>
          {schedules.map((s) => {
            const versions = model.versionsBySchedule[s.ref] || [];
            const timepoints = (s.config?.timepoints || []).length;
            return (
              <div key={s.ref} className="py-1">
                <Row
                  primary={s.name}
                  chips={[`${timepoints} timepoints`, s.is_active ? 'active' : 'inactive']}
                  detail={s.description}
                />
                {versions.length > 0 && (
                  <SubSection id={`${s.ref}/versions`} title="Versions" count={versions.length}>
                    {versions.map((v) => (
                      <Row
                        key={v.ref}
                        primary={`v${v.version_number}${v.version_number === s.current_version_number ? ' (current)' : ''}`}
                        detail={v.reason}
                        chips={v.effective_from ? [String(v.effective_from).slice(0, 10)] : []}
                      />
                    ))}
                  </SubSection>
                )}
              </div>
            );
          })}
        </Section>
      )}

      {reminders.length > 0 && (
        <Section id="reminders" title="Reminder templates" count={reminders.length} action={plannedActions.reminder_template}>
          <ul className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {reminders.map((t) => (
              <Row
                key={t.ref}
                primary={t.name}
                chips={[t.channel, t.is_active ? 'active' : 'inactive'].filter(Boolean)}
                detail={t.template_content}
              />
            ))}
          </ul>
        </Section>
      )}

      {references.length > 0 && (
        <Section
          id="references"
          title="Reference data"
          count={references.length}
          action="match_existing"
          note="must already exist here; never created by the deploy"
        >
          <ul className="divide-y divide-gray-50 dark:divide-gray-700/50">
            {references.map((r, i) => (
              <Row key={i} primary={r.label} chips={[r.kind, r.extra].filter(Boolean)} />
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
};

export default PlannedChangesReview;
